import { useEffect, useState } from 'react';
import {
    useNavigate,
    useLocation,
} from 'react-router-dom';
import styled from 'styled-components';
import { useSnackbar } from "notistack";
import {
    Card,
} from "@mui/material";
import { IconChevronLeft, IconKey } from '@tabler/icons-react';

import { useLicenseHook } from '../../Hooks/License/licenseHooks';

import { LicenseGenerate } from '../../Models/License/License';

import RouteHelper from '../../Utilities/Helpers/RouteHelper';
import RequiredParametersHelper from '../../Utilities/Helpers/RequiredParametersHelper';
import RequiredFieldHelper from '../../Utilities/Helpers/RequiredFieldHelper';
import QueryBuilderHelper from '../../Utilities/Helpers/QueryBuilderHelper';
import LocalStorageHelper from '../../Utilities/Helpers/LocalStorageHelper';
import GlobalFunctionHelper from '../../Utilities/Helpers/GlobalFunctionHelper';

import { ButtonType } from '../../Utilities/Enums/ButtonTypeEnum';
import { RouteEnum } from '../../Utilities/Enums/RouteEnums';
import { GenerateLicenseField } from '../../Utilities/Enums/ParametersEnum';
import { CachingEnum } from '../../Utilities/Enums/CachingEnum';
import { LicenseStatusEnum } from "../../Utilities/Enums/StatusEnums";
import { ValidityTypeEnum } from "../../Utilities/Enums/ValidityTypeEnums";

import CustomButton from '../../CustomViews/Elements/CustomButton';
import CustomDetailsCard from '../../CustomViews/Pages/CustomDetailsCard/CustomDetailsCard';
import BuyLicenseHeader from '../../CustomViews/Pages/CustomTitles/BuyLicenseHeader';
import GeneratedLicenseKey from '../../CustomViews/Pages/GeneratedLicenseKey/GeneratedLicenseKey';

import AutoCompleteDropdown from '../../CustomViews/Elements/AutoCompleteDropdown';
import CompanyDropdown from '../../CustomViews/Elements/AutoComplete/CompanyDropdown';
import UserCapacityDropdown from '../../CustomViews/Elements/AutoComplete/UserCapacityDropdown';
import DataStorageDropdown from '../../CustomViews/Elements/AutoComplete/DataStorageDropdown';
import FormDatePicker from '../../CustomViews/Elements/FormDatePicker';
import FormModulePicker from '../../CustomViews/Elements/FormModulePicker';
import FormValidityField from '../../CustomViews/Elements/FormValidityField';

const CardContainer = styled(Card) <{ $darkMode?: boolean; }>`
    background: transparent !important;
    border: none;
    border-radius: 0px !important;
    display: grid;
    grid-template-columns: auto;
    gap: 30px;
    box-shadow: none !important;
    padding: 10px 0px;
`;

const FormContainer = styled(Card) <{ $darkMode?: boolean; }>`
    box-shadow: none !important;
    background: transparent !important;
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    width: 30%;
    place-self: center;
    justify-items: center;
    overflow: unset !important;

    .customDatePicker {
        width: -webkit-fill-available;

        .MuiStack-root {
            width: -webkit-fill-available;

            .MuiFormControl-root.MuiTextField-root,
            .MuiFormControl-root.MuiPickersTextField-root {
                width: -webkit-fill-available;
            }
        }
    }

    .generateBtn {
        width: -webkit-fill-available;
        margin: 20px 0px 0px 0px;
    }
`;

export default function BuyLicensePackage() {
    //Variables
    let navigate = useNavigate();
    let location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    let requiredFields = RequiredParametersHelper.generateLicense();
    let darkMode = LocalStorageHelper?.isDarkMode() ?? false;

    //Use States
    let [formData, setformData] = useState<LicenseGenerate>(new LicenseGenerate());
    let [errorMessages, setErrorMessages]: any = useState({});
    let [licenseKeyGenerated, setlicenseKeyGenerated]: any = useState<string>('');
    let [licenseKeyGeneratedPublicId, setlicenseKeyGeneratedPublicId]: any = useState<any>('');
    const [queryStr, setQueryStr] = useState({
        pageSize: 10,
        pageNumber: QueryBuilderHelper.DefaultPageNumValue(),
        searchString: QueryBuilderHelper.DefaultSearchValue(),
    });

    const q_queryKey = [CachingEnum.LICENSES, queryStr];
    const { DropdownData, dropdownFetchStatus, generateLicense } = useLicenseHook(q_queryKey, queryStr, false);

    const licenseTypeProps = {
        name: GenerateLicenseField.LICENSE_TYPE,
        title: 'License Type',
        fieldValue: formData?.[GenerateLicenseField.LICENSE_TYPE] ?? "",
        options: DropdownData[CachingEnum.LICENSE_TYPE] ?? [],
        isLoadingDropdown: dropdownFetchStatus ?? false,
        disabled: true,
        required: requiredFields?.find((field: any) => field.keyName == GenerateLicenseField.LICENSE_TYPE)?.keyName != null,
        errorMessages: errorMessages,
        handleOnChange: (selectedValue: any) => {
            setformData(prev => ({ ...prev, [GenerateLicenseField.LICENSE_TYPE]: selectedValue?.id ?? 0 }));
        },
    },
        modulesProps = {
            name: GenerateLicenseField.MODULES,
            title: 'Modules',
            fieldValue: formData?.[GenerateLicenseField.MODULES] ?? [],
            required: requiredFields?.find((field: any) => field.keyName == GenerateLicenseField.MODULES)?.keyName != null,
            errorMessages: errorMessages,
            // hideLabel: true,
            handleOnChange: (selectedValue: any) => {
                setformData(prev => ({ ...prev, [GenerateLicenseField.MODULES]: selectedValue ?? [] }));
            },
        },
        storageCapacityProps = {
            name: GenerateLicenseField.STORAGE_CAPACITY,
            title: 'Data Storage',
            sideContentPosition: 'end',
            sideContent: 'GB',
            fieldValue: formData?.[GenerateLicenseField.STORAGE_CAPACITY] ?? "",
            required: requiredFields?.find((field: any) => field.keyName == GenerateLicenseField.STORAGE_CAPACITY)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                setformData(prev => ({ ...prev, [GenerateLicenseField.STORAGE_CAPACITY]: selectedValue?.id ?? 0 }));
            },
        },
        userCapacityProps = {
            name: GenerateLicenseField.USER_CAPACITY,
            title: 'No. Of Users',
            sideContentPosition: 'end',
            sideContent: 'Users',
            fieldValue: formData?.[GenerateLicenseField.USER_CAPACITY] ?? "",
            required: requiredFields?.find((field: any) => field.keyName == GenerateLicenseField.USER_CAPACITY)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                setformData(prev => ({ ...prev, [GenerateLicenseField.USER_CAPACITY]: selectedValue?.id ?? 0 }));
            },
        },
        validityProps = {
            validityTitle: 'Validity',
            validityName: GenerateLicenseField.VALIDITY,
            validityValue: formData?.[GenerateLicenseField.VALIDITY] ?? "",
            validityRequired: requiredFields?.find((field: any) => field.keyName == GenerateLicenseField.VALIDITY)?.keyName != null,
            validityTypeTitle: 'Validity Type',
            validityTypeName: GenerateLicenseField.VALIDITY_TYPE,
            validityTypeValue: formData?.[GenerateLicenseField.VALIDITY_TYPE] ?? "",
            validityTypeRequired: requiredFields?.find((field: any) => field.keyName == GenerateLicenseField.VALIDITY_TYPE)?.keyName != null,
            errorMessages: errorMessages,
            hideLabel: false,
            disabled: false,
            validityHandleOnChange: (selectedValue: any) => {
                let fromNow = new Date();
                let _validityType = formData?.[GenerateLicenseField.VALIDITY_TYPE] ?? ValidityTypeEnum.MONTH;
                let _plusAmount = isNaN(parseFloat(selectedValue)) ? 0 : parseFloat(selectedValue);

                if(_validityType == ValidityTypeEnum.MONTH) {
                    fromNow.setMonth(fromNow.getMonth() + _plusAmount);
                }

                if(_validityType == ValidityTypeEnum.YEAR) {
                    fromNow.setFullYear(fromNow.getFullYear() + _plusAmount);
                }

                setformData(prev => ({ ...prev, [GenerateLicenseField.VALIDITY]: selectedValue ?? 0, [GenerateLicenseField.EXPIRY_DATE]: fromNow, }));
            },
            validityTypeHandleOnChange: (selectedValue: any) => {
                let fromNow = new Date();
                let _validityType = selectedValue?.id ?? ValidityTypeEnum.MONTH;
                let _validity: any = formData?.[GenerateLicenseField.VALIDITY] ?? "0";
                let _plusAmount: number = isNaN(parseFloat(_validity)) ? 0 : parseFloat(_validity);

                if(_validityType == ValidityTypeEnum.MONTH) {
                    fromNow.setMonth(fromNow.getMonth() + _plusAmount);
                }

                if(_validityType == ValidityTypeEnum.YEAR) {
                    fromNow.setFullYear(fromNow.getFullYear() + _plusAmount);
                }

                setformData(prev => ({ ...prev, [GenerateLicenseField.VALIDITY_TYPE]: selectedValue?.id ?? 0, [GenerateLicenseField.EXPIRY_DATE]: fromNow, }));
            },
        },
        issueDateProps = {
            name: GenerateLicenseField.ISSUE_DATE,
            title: 'License Issue Date',
            fieldValue: formData?.[GenerateLicenseField.ISSUE_DATE] ?? null,
            required: requiredFields?.find((field: any) => field.keyName == GenerateLicenseField.ISSUE_DATE)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                setformData(prev => ({ ...prev, [GenerateLicenseField.ISSUE_DATE]: selectedValue ?? null }));
            },
        },
        expiryDateProps = {
            name: GenerateLicenseField.EXPIRY_DATE,
            title: 'License Expiry Date',
            fieldValue: formData?.[GenerateLicenseField.EXPIRY_DATE] ?? null,
            required: requiredFields?.find((field: any) => field.keyName == GenerateLicenseField.EXPIRY_DATE)?.keyName != null,
            disabled: true,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                setformData(prev => ({ ...prev, [GenerateLicenseField.EXPIRY_DATE]: selectedValue ?? null }));
            },
        },
        issuedCompanyProps = {
            name: GenerateLicenseField.COMPANY_PUBLIC_ID,
            title: 'Issued Company',
            fieldValue: formData?.[GenerateLicenseField.COMPANY_PUBLIC_ID] ?? "",
            required: requiredFields?.find((field: any) => field.keyName == GenerateLicenseField.COMPANY_PUBLIC_ID)?.keyName != null,
            errorMessages: errorMessages,
            handleOnChange: (selectedValue: any) => {
                setformData(prev => ({ ...prev, [GenerateLicenseField.COMPANY_PUBLIC_ID]: selectedValue?.id ?? null }));
            },
        };

    //Event Handlers
    const initializeData = async () => {
        let _formData = {
            ...formData,
            [GenerateLicenseField.LICENSE_TYPE]: 1,
            [GenerateLicenseField.STATUS]: LicenseStatusEnum.UNUSED,
            [GenerateLicenseField.ISSUE_DATE]: new Date(),
        };

        setformData(_formData);
    }
    
    const handleSubmit = async () => {
        let _validateForm = RequiredFieldHelper.validateForm(requiredFields, formData, errorMessages, setErrorMessages, enqueueSnackbar);

        if (_validateForm?.hasError) { return true; }

        let generateResponse = await generateLicense(JSON.stringify(formData));
        
        if(generateResponse?.isSuccess) {
            setlicenseKeyGenerated(generateResponse?.data?.data?.[GenerateLicenseField.GENERATED_LICENSE_KEY] ?? "");
            setlicenseKeyGeneratedPublicId(generateResponse?.data?.data?.[GenerateLicenseField.PUBLIC_ID] ?? "");
        }
    }

    useEffect(() => {
        initializeData();
    }, []);

    //Rendering Functions
    const renderActionButtons = () => {
        return <>
            <CustomButton
                btnType={ButtonType.CANCEL}
                title='Back'
                startIcon={<IconChevronLeft />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    GlobalFunctionHelper.goBack(navigate, location, RouteEnum.LICENSES);
                }} />
        </>
    }

    const renderModules = () => {
        return <FormModulePicker {...modulesProps} />
    }

    const renderForm = () => {
        return <FormContainer>
            <BuyLicenseHeader
                headerTitle={"Generate a New License Key"}
                headerSubTitle={"Please confirm that the information is correct before generating."} />

            <AutoCompleteDropdown {...licenseTypeProps} />

            {renderModules()}

            <UserCapacityDropdown {...userCapacityProps} />
            <DataStorageDropdown {...storageCapacityProps} />
            {/*<AutoCompleteDropdown {...validityProps} />*/}
            <FormValidityField {...validityProps} />
            <FormDatePicker {...issueDateProps} />
            <FormDatePicker {...expiryDateProps} />
            <CompanyDropdown {...issuedCompanyProps} />

            <CustomButton
                className="generateBtn"
                btnType={ButtonType.SUCCESS}
                title='Generate License Now'
                startIcon={<IconKey />}
                // isRoundedCorner={true}
                onClick={(e: any) => {
                    handleSubmit();
                }} />
        </FormContainer>
    }

    return <CardContainer>
        <CustomDetailsCard
        title={RouteHelper.getRouteDescription(RouteEnum.GENERATE_LICENSE)}
        actionButtons={renderActionButtons()} >
            {(licenseKeyGenerated == null || licenseKeyGenerated == "") && renderForm()}

            {(licenseKeyGenerated != null && licenseKeyGenerated != "") && <GeneratedLicenseKey
                headerTitle={"License Generated"}
                headerSubTitle={"You have successfully generated a new license."}
                licenseKey={licenseKeyGenerated}
                onclickLicenseKey={() => {
                    if(licenseKeyGeneratedPublicId != null && licenseKeyGeneratedPublicId != "") {
                        navigate(`${RouteEnum.LICENSE_DETAILS}?id=${licenseKeyGeneratedPublicId}`, { replace: true });
                    }
                }} />}
        </CustomDetailsCard>
    </CardContainer>
}
